import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { aiApi, patientSelfServiceApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Heart, Activity, Pill, Calendar, MessageSquare, FileText,
  TrendingUp, TrendingDown, AlertCircle, Clock, Send, Sparkles,
  User, Phone, Mail, ChevronRight, Download, DollarSign, Edit,
  RefreshCw, CheckCircle, FileDown, Plus
} from 'lucide-react';
import { toast } from 'sonner';

export default function PatientPortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [aiMessage, setAiMessage] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  // Backend-backed patient data.
  const [healthData, setHealthData] = useState({
    patient: {}, vitals: [], medications: [], appointments: [], lab_results: [], clinical_notes: [],
  });
  // Self-service state
  const [records, setRecords] = useState(null);
  const [billing, setBilling] = useState(null);
  const [messages, setMessages] = useState([]);
  const [documentRequests, setDocumentRequests] = useState([]);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showDiagnosisDialog, setShowDiagnosisDialog] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [diagnosisDraft, setDiagnosisDraft] = useState('');
  const [requestForm, setRequestForm] = useState({ document_type: 'medical_records', delivery_method: 'download', reason: '' });
  const [profileForm, setProfileForm] = useState({ phone: '', address: '', emergency_contact_name: '', emergency_contact_phone: '' });
  const [messageForm, setMessageForm] = useState({ subject: '', message: '' });
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [savingDiag, setSavingDiag] = useState(false);

  useEffect(() => {
    loadSelfServiceData();
  }, []);

  const loadSelfServiceData = async () => {
    try {
      const [recordsRes, billingRes, msgsRes, docReqRes] = await Promise.all([
        patientSelfServiceApi.getRecords().catch(() => ({ data: null })),
        patientSelfServiceApi.getBilling().catch(() => ({ data: { bills: [], total_owed: 0 } })),
        patientSelfServiceApi.getMessages().catch(() => ({ data: { messages: [] } })),
        patientSelfServiceApi.listDocumentRequests().catch(() => ({ data: { requests: [] } })),
      ]);
      setRecords(recordsRes.data);
      setBilling(billingRes.data);
      setMessages(msgsRes.data.messages || []);
      setDocumentRequests(docReqRes.data.requests || []);
      if (recordsRes.data) {
        setHealthData({
          patient: recordsRes.data.patient || {},
          vitals: recordsRes.data.vitals || [],
          medications: recordsRes.data.medications || [],
          appointments: recordsRes.data.appointments || [],
          lab_results: recordsRes.data.lab_results || [],
          clinical_notes: recordsRes.data.clinical_notes || [],
        });
        setDiagnosisDraft(recordsRes.data.patient?.medical_diagnosis || '');
      }
    } catch (err) {
      console.error('Error loading self-service data:', err);
    }
  };

  const handleDownloadRecord = async () => {
    try {
      setDownloadingPdf(true);
      const res = await patientSelfServiceApi.downloadRecordPdf();
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my_medical_record.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Record downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not download record');
    } finally { setDownloadingPdf(false); }
  };

  const handleSaveDiagnosis = async () => {
    try {
      setSavingDiag(true);
      await patientSelfServiceApi.updateProfile({ medical_diagnosis: diagnosisDraft });
      setHealthData(hd => ({ ...hd, patient: { ...hd.patient, medical_diagnosis: diagnosisDraft } }));
      toast.success('Diagnosis updated — your care team has been notified');
      setShowDiagnosisDialog(false);
    } catch {
      toast.error('Failed to update diagnosis');
    } finally { setSavingDiag(false); }
  };

  const handleSubmitDocumentRequest = async () => {
    try {
      await patientSelfServiceApi.requestDocument(requestForm);
      toast.success('Document request submitted');
      setShowRequestDialog(false);
      setRequestForm({ document_type: 'medical_records', delivery_method: 'download', reason: '' });
      // Reload the requests list
      const res = await patientSelfServiceApi.listDocumentRequests().catch(() => ({ data: { requests: [] } }));
      setDocumentRequests(res.data.requests || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit request');
    }
  };

  const handleProfileUpdate = async () => {
    try {
      const updateData = {};
      if (profileForm.phone) updateData.phone = profileForm.phone;
      if (profileForm.address) updateData.address = profileForm.address;
      if (profileForm.emergency_contact_name) updateData.emergency_contact_name = profileForm.emergency_contact_name;
      if (profileForm.emergency_contact_phone) updateData.emergency_contact_phone = profileForm.emergency_contact_phone;
      if (Object.keys(updateData).length === 0) { toast.error('No changes to save'); return; }
      await patientSelfServiceApi.updateProfile(updateData);
      toast.success('Profile updated');
      setShowEditProfile(false);
      loadSelfServiceData();
    } catch (err) {
      toast.error('Failed to update profile');
    }
  };

  const handleSendMessage = async () => {
    if (!messageForm.subject || !messageForm.message) { toast.error('Subject and message required'); return; }
    try {
      await patientSelfServiceApi.sendMessage(messageForm);
      toast.success('Message sent to your care team');
      setShowMessageDialog(false);
      setMessageForm({ subject: '', message: '' });
      loadSelfServiceData();
    } catch (err) {
      toast.error('Failed to send message');
    }
  };

  const handleAiChat = async (e) => {
    e.preventDefault();
    if (!aiMessage.trim()) return;
    
    setAiLoading(true);
    try {
      const response = await aiApi.chat(aiMessage);
      setAiResponse(response.data.response);
      setAiMessage('');
    } catch (error) {
      toast.error('Health assistant temporarily unavailable');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="patient-portal">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-[#0A2342] to-[#0E7490] rounded-2xl p-8 text-white">
        <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Welcome, {user?.name}
        </h1>
        <p className="text-white/80">
          Here's your health summary. Your next appointment is on January 20th.
        </p>
        <div className="flex flex-wrap gap-4 mt-6">
          <Button className="bg-white text-[#0A2342] hover:bg-white/90" onClick={() => navigate('/appointments')} data-testid="schedule-appointment-btn">
            <Calendar className="w-4 h-4 mr-2" />
            Schedule Appointment
          </Button>
          <Button variant="outline" className="border-white text-white hover:bg-white/10" onClick={() => navigate('/messages')}>
            <MessageSquare className="w-4 h-4 mr-2" />
            Message Care Team
          </Button>
        </div>
      </div>

      {/* Action Items - Clickable Drill-downs */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="careiq-card border-l-4 border-l-[#0E7490] cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setShowDiagnosisDialog(true)} data-testid="drilldown-my-diagnosis">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-[#0E7490]/10 rounded-lg flex items-center justify-center">
              <Heart className="w-5 h-5 text-[#0E7490]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[#0A2342]">My Diagnosis</div>
              <div className="text-sm text-[#64748B] truncate">
                {healthData.patient?.medical_diagnosis || 'Tap to add your diagnosis'}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#64748B]" />
          </CardContent>
        </Card>

        <Card className="careiq-card border-l-4 border-l-[#92400E] cursor-pointer hover:shadow-lg transition-shadow" onClick={handleDownloadRecord} data-testid="drilldown-download-record">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-[#92400E]/10 rounded-lg flex items-center justify-center">
              <FileDown className="w-5 h-5 text-[#92400E]" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-[#0A2342]">Download My Record</div>
              <div className="text-sm text-[#64748B]">PDF summary you can share with any provider</div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#64748B]" />
          </CardContent>
        </Card>

        <Card className="careiq-card border-l-4 border-l-[#166534] cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/appointments')} data-testid="drilldown-next-appointment">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-[#166534]/10 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-[#166534]" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-[#0A2342]">Next appointment</div>
              <div className="text-sm text-[#64748B]">
                {healthData.appointments?.[0]
                  ? `${(healthData.appointments[0].scheduled_date || '').slice(0, 10)} at ${healthData.appointments[0].scheduled_time || ''}`
                  : 'No appointments scheduled'}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#64748B]" />
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Health at a Glance */}
          <Card className="careiq-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#991B1B]" />
                My Health at a Glance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {healthData.vitals.length === 0 ? (
                <p className="text-sm text-[#94A3B8] text-center py-4">No vitals recorded yet. Ask your care team to share your latest measurements.</p>
              ) : (
                <div className="space-y-3">
                  {healthData.vitals.slice(0, 5).map((v, index) => (
                    <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-[#F8FAFC] text-sm">
                      <div className="text-xs text-[#64748B]">
                        {v.recorded_at ? new Date(v.recorded_at).toLocaleDateString() : '—'}
                      </div>
                      <div className="flex gap-4 text-[13px]">
                        {v.blood_pressure_systolic && <div><span className="text-[#64748B]">BP</span> <b>{v.blood_pressure_systolic}/{v.blood_pressure_diastolic}</b></div>}
                        {v.heart_rate && <div><span className="text-[#64748B]">HR</span> <b>{v.heart_rate}</b></div>}
                        {v.temperature && <div><span className="text-[#64748B]">T</span> <b>{v.temperature}°F</b></div>}
                        {v.oxygen_saturation && <div><span className="text-[#64748B]">SpO2</span> <b>{v.oxygen_saturation}%</b></div>}
                        {v.weight && <div><span className="text-[#64748B]">Wt</span> <b>{v.weight}</b></div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Records Tabs */}
          <Card className="careiq-card">
            <Tabs defaultValue="diagnosis">
              <CardHeader className="pb-0">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="diagnosis" data-testid="tab-diagnosis">My Diagnosis</TabsTrigger>
                  <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
                  <TabsTrigger value="appointments" data-testid="tab-appointments">Appointments</TabsTrigger>
                </TabsList>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Diagnosis — patient-editable */}
                <TabsContent value="diagnosis" className="m-0 space-y-3" data-testid="tab-content-diagnosis">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1">Current diagnosis on file</div>
                      <div className="p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] text-sm text-[#0A2342] min-h-[44px] whitespace-pre-wrap" data-testid="diagnosis-display">
                        {healthData.patient?.medical_diagnosis || <span className="text-[#94A3B8] italic">Not yet provided</span>}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setShowDiagnosisDialog(true)} data-testid="edit-diagnosis-btn">
                      <Edit className="w-3 h-3 mr-1" />Update
                    </Button>
                  </div>
                  <p className="text-[11px] text-[#64748B] leading-relaxed">
                    Keeping this up to date helps your entire care team see a single, accurate picture of your health. Your care team will be notified when you make changes.
                  </p>
                </TabsContent>

                {/* Document requests */}
                <TabsContent value="documents" className="m-0 space-y-3" data-testid="tab-content-documents">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-[#0A2342]">Medical record requests</h3>
                      <p className="text-xs text-[#64748B]">Ask your care team to prepare specific documents.</p>
                    </div>
                    <Button size="sm" className="careiq-btn-primary" onClick={() => setShowRequestDialog(true)} data-testid="request-document-btn">
                      <Plus className="w-3 h-3 mr-1" />New Request
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {documentRequests.length === 0 ? (
                      <div className="text-center py-6 text-sm text-[#94A3B8]" data-testid="no-document-requests">
                        No requests yet. Click <b>New Request</b> to ask for records, a visit summary, or a referral letter.
                      </div>
                    ) : documentRequests.map(r => (
                      <div key={r.id} className="p-3 rounded-lg bg-[#F8FAFC] flex items-center justify-between" data-testid={`document-request-${r.id}`}>
                        <div>
                          <div className="font-medium text-sm text-[#0A2342] capitalize">{(r.document_type || '').replace(/_/g, ' ')}</div>
                          <div className="text-[11px] text-[#64748B]">
                            {r.created_at ? new Date(r.created_at).toLocaleString() : ''} · {r.delivery_method || 'download'}
                          </div>
                          {r.reason && <div className="text-[11px] text-[#475569] mt-0.5 italic">"{r.reason}"</div>}
                        </div>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                          r.status === 'fulfilled' ? 'bg-[#166534]/10 text-[#166534]' :
                          r.status === 'declined' ? 'bg-[#991B1B]/10 text-[#991B1B]' :
                          'bg-[#F59E0B]/10 text-[#92400E]'
                        }`}>
                          {r.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="appointments" className="m-0">
                  <div className="space-y-3">
                    {(healthData.appointments || []).length === 0 ? (
                      <div className="text-center py-6 text-sm text-[#94A3B8]">No upcoming appointments</div>
                    ) : (healthData.appointments || []).slice(0, 10).map((appt, index) => (
                      <div key={index} className="flex items-center justify-between p-4 rounded-lg bg-[#F8FAFC]">
                        <div>
                          <div className="font-medium text-[#0A2342]">{appt.appointment_type || 'Visit'}</div>
                          <div className="text-sm text-[#64748B]">{appt.provider_name || appt.provider || ''}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-[#0A2342]">{(appt.scheduled_date || '').slice(0, 10)}</div>
                          <div className="text-sm text-[#64748B]">{appt.scheduled_time || appt.time || ''}</div>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" className="w-full careiq-btn-outline mt-4" onClick={() => navigate('/appointments')}>
                      <Calendar className="w-4 h-4 mr-2" />
                      Schedule New Appointment
                    </Button>
                  </div>
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* AI Health Assistant */}
          <Card className="careiq-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#0E7490]" />
                Health Assistant
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-40 mb-4 p-3 bg-[#F8FAFC] rounded-lg">
                {aiResponse ? (
                  <div className="text-sm text-[#334155] whitespace-pre-wrap">{aiResponse}</div>
                ) : (
                  <div className="text-center text-[#64748B] py-4">
                    <p className="text-sm">Ask questions about your health, medications, or test results.</p>
                    <p className="text-xs mt-2 text-[#94A3B8]">This is for information only — always consult your care team.</p>
                  </div>
                )}
              </ScrollArea>
              <form onSubmit={handleAiChat} className="flex gap-2">
                <Input
                  placeholder="Ask a question..."
                  value={aiMessage}
                  onChange={(e) => setAiMessage(e.target.value)}
                  className="careiq-input"
                  disabled={aiLoading}
                  data-testid="patient-ai-input"
                />
                <Button 
                  type="submit" 
                  className="careiq-btn-secondary"
                  disabled={aiLoading || !aiMessage.trim()}
                >
                  {aiLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Care Team */}
          <Card className="careiq-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#0A2342]">My Care Team</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#0E7490] rounded-full flex items-center justify-center text-white font-medium">
                    SJ
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-[#0A2342]">Dr. Sarah Johnson</div>
                    <div className="text-sm text-[#64748B]">Primary Care</div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-[#0E7490]" onClick={() => setShowMessageDialog(true)}>
                    <MessageSquare className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#1A56DB] rounded-full flex items-center justify-center text-white font-medium">
                    MG
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-[#0A2342]">Maria Garcia, RN</div>
                    <div className="text-sm text-[#64748B]">Care Coordinator</div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-[#0E7490]" onClick={() => setShowMessageDialog(true)}>
                    <MessageSquare className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Self-Service Section */}
      <Card className="careiq-card" data-testid="self-service-section">
        <Tabs defaultValue="profile">
          <CardHeader className="pb-0">
            <CardTitle className="text-lg font-semibold text-[#0A2342] mb-3">Self-Service</CardTitle>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="profile" data-testid="tab-profile">
                <User className="w-4 h-4 mr-1 hidden sm:inline" /> Profile
              </TabsTrigger>
              <TabsTrigger value="records" data-testid="tab-records">
                <FileText className="w-4 h-4 mr-1 hidden sm:inline" /> Records
              </TabsTrigger>
              <TabsTrigger value="messages" data-testid="tab-messages">
                <MessageSquare className="w-4 h-4 mr-1 hidden sm:inline" /> Messages
              </TabsTrigger>
              <TabsTrigger value="billing" data-testid="tab-billing">
                <DollarSign className="w-4 h-4 mr-1 hidden sm:inline" /> Billing
              </TabsTrigger>
            </TabsList>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Profile Tab */}
            <TabsContent value="profile" className="m-0">
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-[#F8FAFC] rounded-xl">
                  <div className="w-16 h-16 bg-[#0E7490] rounded-full flex items-center justify-center text-white text-xl font-semibold">
                    {user?.name?.charAt(0) || 'P'}
                  </div>
                  <div className="flex-1">
                    <div className="text-lg font-semibold text-[#0A2342]">{user?.name}</div>
                    <div className="text-sm text-[#64748B]">{user?.email}</div>
                    <div className="flex gap-4 mt-1 text-xs text-[#64748B]">
                      {profileForm.phone && <span><Phone className="w-3 h-3 inline mr-1" />{profileForm.phone}</span>}
                      {profileForm.address && <span><Mail className="w-3 h-3 inline mr-1" />{profileForm.address}</span>}
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => setShowEditProfile(true)} data-testid="edit-profile-btn">
                    <Edit className="w-4 h-4 mr-2" /> Edit Profile
                  </Button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="p-3 bg-[#F8FAFC] rounded-lg">
                    <div className="text-xs text-[#64748B]">Emergency Contact</div>
                    <div className="text-sm font-medium text-[#0A2342] mt-1">
                      {profileForm.emergency_contact_name || 'Not set'}
                    </div>
                    {profileForm.emergency_contact_phone && (
                      <div className="text-xs text-[#64748B]">{profileForm.emergency_contact_phone}</div>
                    )}
                  </div>
                  <div className="p-3 bg-[#F8FAFC] rounded-lg">
                    <div className="text-xs text-[#64748B]">Member Since</div>
                    <div className="text-sm font-medium text-[#0A2342] mt-1">January 2024</div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Records Tab */}
            <TabsContent value="records" className="m-0">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-[#64748B]">Download a PDF of your medical record</p>
                  <Button onClick={handleDownloadRecord} disabled={downloadingPdf} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="download-records-btn">
                    <Download className="w-4 h-4 mr-2" /> {downloadingPdf ? 'Preparing…' : 'Download Records'}
                  </Button>
                </div>
                {records ? (
                  <div className="space-y-3">
                    <div className="p-4 bg-[#F8FAFC] rounded-lg">
                      <div className="font-medium text-[#0A2342]">Personal Information</div>
                      <div className="text-sm text-[#64748B] mt-1">Name, DOB, contact details, insurance</div>
                    </div>
                    <div className="p-4 bg-[#F8FAFC] rounded-lg">
                      <div className="font-medium text-[#0A2342]">Medical Diagnosis</div>
                      <div className="text-sm text-[#64748B] mt-1">{healthData.patient?.medical_diagnosis || 'Not yet provided'}</div>
                    </div>
                    {records.vitals && records.vitals.length > 0 && (
                      <div className="p-4 bg-[#F8FAFC] rounded-lg">
                        <div className="font-medium text-[#0A2342]">Vitals ({records.vitals.length})</div>
                        <div className="text-sm text-[#64748B] mt-1">Recent measurements</div>
                      </div>
                    )}
                    {records.appointments && records.appointments.length > 0 && (
                      <div className="p-4 bg-[#F8FAFC] rounded-lg">
                        <div className="font-medium text-[#0A2342]">Appointments ({records.appointments.length})</div>
                        <div className="text-sm text-[#64748B] mt-1">Upcoming and recent visits</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#64748B]">
                    <FileText className="w-10 h-10 mx-auto mb-2 text-[#94A3B8]" />
                    <p className="text-sm">No records available yet</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Messages Tab */}
            <TabsContent value="messages" className="m-0">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-[#64748B]">Secure messages with your care team</p>
                  <Button onClick={() => setShowMessageDialog(true)} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="send-message-btn">
                    <Send className="w-4 h-4 mr-2" /> New Message
                  </Button>
                </div>
                {messages.length > 0 ? (
                  <ScrollArea className="max-h-72">
                    <div className="space-y-3">
                      {messages.map((msg, i) => (
                        <div key={i} className="p-4 bg-[#F8FAFC] rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="font-medium text-sm text-[#0A2342]">{msg.subject}</div>
                            <span className="text-[10px] text-[#64748B]">{msg.created_at ? new Date(msg.created_at).toLocaleDateString() : ''}</span>
                          </div>
                          <p className="text-xs text-[#64748B] mt-1 line-clamp-2">{msg.message}</p>
                          {msg.reply && (
                            <div className="mt-2 p-2 bg-[#0E7490]/5 rounded border-l-2 border-[#0E7490]">
                              <div className="text-[10px] text-[#0E7490] font-medium">Care Team Reply</div>
                              <p className="text-xs text-[#64748B] mt-0.5">{msg.reply}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-8 text-[#64748B]">
                    <MessageSquare className="w-10 h-10 mx-auto mb-2 text-[#94A3B8]" />
                    <p className="text-sm">No messages yet</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Billing Tab */}
            <TabsContent value="billing" className="m-0">
              <div className="space-y-4">
                {billing ? (
                  <>
                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#0A2342] to-[#0E7490] rounded-xl text-white">
                      <div>
                        <div className="text-sm opacity-80">Total Balance Due</div>
                        <div className="text-3xl font-bold mt-1">${billing.total_owed?.toFixed(2) || '0.00'}</div>
                      </div>
                      <DollarSign className="w-10 h-10 opacity-30" />
                    </div>
                    {billing.bills?.length > 0 ? (
                      <div className="space-y-3">
                        {billing.bills.map((bill, i) => (
                          <div key={i} className="flex items-center justify-between p-4 bg-[#F8FAFC] rounded-lg">
                            <div>
                              <div className="font-medium text-sm text-[#0A2342]">{bill.description}</div>
                              <div className="text-xs text-[#64748B]">{bill.date || bill.service_date}</div>
                            </div>
                            <div className="text-right">
                              <div className="font-medium text-[#0A2342]">${bill.amount?.toFixed(2)}</div>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                                bill.status === 'paid' ? 'bg-[#166534]/10 text-[#166534]' :
                                'bg-[#92400E]/10 text-[#92400E]'
                              }`}>
                                {bill.status || 'unpaid'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-[#64748B] text-center py-4">No bills on record</p>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 text-[#64748B]">
                    <DollarSign className="w-10 h-10 mx-auto mb-2 text-[#94A3B8]" />
                    <p className="text-sm">Billing information unavailable</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditProfile} onOpenChange={setShowEditProfile}>
        <DialogContent className="sm:max-w-md" aria-describedby="edit-profile-desc">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
            <p id="edit-profile-desc" className="text-sm text-[#64748B]">Update your contact information</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Phone Number</Label>
              <Input value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} placeholder="(555) 123-4567" className="mt-1" data-testid="profile-phone-input" />
            </div>
            <div>
              <Label>Address</Label>
              <Input value={profileForm.address} onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })} placeholder="123 Main St, City, ST" className="mt-1" data-testid="profile-address-input" />
            </div>
            <div>
              <Label>Emergency Contact Name</Label>
              <Input value={profileForm.emergency_contact_name} onChange={(e) => setProfileForm({ ...profileForm, emergency_contact_name: e.target.value })} placeholder="Jane Doe" className="mt-1" data-testid="profile-emergency-name" />
            </div>
            <div>
              <Label>Emergency Contact Phone</Label>
              <Input value={profileForm.emergency_contact_phone} onChange={(e) => setProfileForm({ ...profileForm, emergency_contact_phone: e.target.value })} placeholder="(555) 987-6543" className="mt-1" data-testid="profile-emergency-phone" />
            </div>
            <Button className="w-full bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={handleProfileUpdate} data-testid="save-profile-btn">
              <CheckCircle className="w-4 h-4 mr-2" /> Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Diagnosis Dialog */}
      <Dialog open={showDiagnosisDialog} onOpenChange={setShowDiagnosisDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="diagnosis-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Heart className="w-5 h-5 text-[#0E7490]" />Update My Diagnosis</DialogTitle>
            <p className="text-sm text-[#64748B]">This appears on your Overview and is shared with your care team. Add any active conditions your providers should know about.</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Textarea
              value={diagnosisDraft}
              onChange={(e) => setDiagnosisDraft(e.target.value)}
              placeholder="e.g., Type 2 Diabetes, Hypertension, Hypothyroidism"
              className="min-h-[120px] text-sm"
              data-testid="diagnosis-textarea"
            />
            <p className="text-[11px] text-[#94A3B8] leading-relaxed">
              Note: Your care team may update this after reviewing your records. Any changes you make here are logged to the HIPAA audit trail.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDiagnosisDialog(false)} disabled={savingDiag} data-testid="diagnosis-cancel">Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handleSaveDiagnosis} disabled={savingDiag} data-testid="diagnosis-save">
                {savingDiag ? 'Saving…' : 'Save Diagnosis'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Document Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="sm:max-w-md" data-testid="request-document-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-[#0E7490]" />Request a Document</DialogTitle>
            <p className="text-sm text-[#64748B]">Your care team will prepare the record and notify you when it's ready.</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>What do you need?</Label>
              <select
                value={requestForm.document_type}
                onChange={(e) => setRequestForm({ ...requestForm, document_type: e.target.value })}
                className="w-full mt-1 rounded-md border border-[#E2E8F0] p-2 text-sm"
                data-testid="request-document-type"
              >
                <option value="medical_records">Full medical records</option>
                <option value="visit_summary">Visit summary</option>
                <option value="immunization_records">Immunization records</option>
                <option value="lab_results">Lab results</option>
                <option value="referral_letter">Referral letter</option>
                <option value="disability_form">Disability / FMLA form</option>
                <option value="insurance_documentation">Insurance documentation</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label>How would you like to receive it?</Label>
              <select
                value={requestForm.delivery_method}
                onChange={(e) => setRequestForm({ ...requestForm, delivery_method: e.target.value })}
                className="w-full mt-1 rounded-md border border-[#E2E8F0] p-2 text-sm"
                data-testid="request-document-delivery"
              >
                <option value="download">Download from my portal</option>
                <option value="email">Email to me</option>
                <option value="mail">Mail to my address on file</option>
              </select>
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Textarea
                value={requestForm.reason}
                onChange={(e) => setRequestForm({ ...requestForm, reason: e.target.value })}
                placeholder="e.g., school enrollment, new specialist, insurance claim"
                className="mt-1 min-h-[70px] text-sm"
                data-testid="request-document-reason"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRequestDialog(false)} data-testid="request-document-cancel">Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handleSubmitDocumentRequest} data-testid="request-document-submit">Submit Request</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="sm:max-w-md" aria-describedby="message-desc">
          <DialogHeader>
            <DialogTitle>Message Care Team</DialogTitle>
            <p id="message-desc" className="text-sm text-[#64748B]">Send a secure message to your care team</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Subject</Label>
              <Input value={messageForm.subject} onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })} placeholder="e.g., Question about medication" className="mt-1" data-testid="message-subject-input" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea value={messageForm.message} onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })} placeholder="Type your message here..." className="mt-1" rows={4} data-testid="message-body-input" />
            </div>
            <Button className="w-full bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={handleSendMessage} data-testid="send-message-submit-btn">
              <Send className="w-4 h-4 mr-2" /> Send Message
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
